/**
 * Simple-mode DataTable: font/column density for narrow viewports (MMEC simple shell).
 * Persisted per browser so scanner tab remembers the choice.
 */

export type SimpleScannerTableDensity = 'standard' | 'compact' | 'dense';

export const MMEC_SIMPLE_SCANNER_TABLE_DENSITY_KEY = 'mmec_simple_scanner_table_density_v1';

export function loadSimpleScannerTableDensity(): SimpleScannerTableDensity {
  try {
    const v = localStorage.getItem(MMEC_SIMPLE_SCANNER_TABLE_DENSITY_KEY);
    if (v === 'standard' || v === 'compact' || v === 'dense') return v;
  } catch {
    /* ignore */
  }
  /** Default a bit tighter than legacy 120px / 14px so phones fit more without opening settings. */
  return 'compact';
}

export function saveSimpleScannerTableDensity(d: SimpleScannerTableDensity): void {
  try {
    localStorage.setItem(MMEC_SIMPLE_SCANNER_TABLE_DENSITY_KEY, d);
  } catch {
    /* ignore */
  }
}

export interface SimpleTableDensityMetrics {
  cellPadding: string;
  headerPadding: string;
  bodyFontPx: number;
  headerLabelPx: number;
  sortBtnFontPx: number;
  /** Basewidth when column config uses default ~120px (scaled against 120). */
  defaultColWidth: number;
  minColFloor: number;
  /** Tailwind classes for the simple-mode toolbar row (padding + FAB gutter). */
  toolbarClassName: string;
  /** Consolidated table: multiply fixed minWidth baseline (1080 / 1280). */
  consolidatedTableScale: number;
}

export function getSimpleTableDensityMetrics(d: SimpleScannerTableDensity): SimpleTableDensityMetrics {
  switch (d) {
    case 'dense':
      return {
        cellPadding: '3px 5px',
        headerPadding: '4px 6px',
        bodyFontPx: 10,
        headerLabelPx: 10,
        sortBtnFontPx: 11,
        defaultColWidth: 72,
        minColFloor: 52,
        toolbarClassName: 'py-1 pl-11 pr-1.5 gap-1',
        consolidatedTableScale: 0.62,
      };
    case 'compact':
      return {
        cellPadding: '5px 7px',
        headerPadding: '6px 8px',
        bodyFontPx: 11,
        headerLabelPx: 11,
        sortBtnFontPx: 12,
        defaultColWidth: 90,
        minColFloor: 60,
        toolbarClassName: 'py-1.5 pl-12 pr-2 gap-1.5',
        consolidatedTableScale: 0.78,
      };
    default:
      return {
        cellPadding: '8px 12px',
        headerPadding: '8px 12px',
        bodyFontPx: 13,
        headerLabelPx: 14,
        sortBtnFontPx: 14,
        defaultColWidth: 120,
        minColFloor: 80,
        toolbarClassName: 'py-2 pl-14 pr-3 gap-2',
        consolidatedTableScale: 1,
      };
  }
}
